import React from 'react'
import {
  Css,
  Container,
  TextBody,
  TextCaption,
  TextTitle,
  Link,
  Button,
  Icon,
  State
} from '@pmi/pmi-iqos-ds-react'
import {
  LazyImage,
  formatCurrency,
  htmlTranslation,
  isAuthenticated,
  getDynamicPath,
  goToRoute,
  Routes
} from '@pmi/pmi-iqos-shared-react'
import { Highlight, useInfiniteHits } from 'react-instantsearch'
import {
  IndexNameTypes,
  IndexIdTypes,
  RECOMMENDED_CONTENT_ID,
  INITIAL_QUERY_SUGGESTIONS_ID,
  isAV
} from './utils'
import { DcxAnalytics } from '@dcx-common/services'

const Header = ({ text }) => (
  <Container pt="10">
    <TextCaption size={TextCaption.sizes.s}>{text}</TextCaption>
  </Container>
)

const ItemLink = ({
  hit,
  components,
  indexName,
  indexId,
  fireSearchClickGA
}) => {
  const clickHandler = () => {
    indexId !== IndexIdTypes.RECENT_SEARCHES && fireSearchClickGA()
    if (indexId === IndexIdTypes.RECENT_SEARCHES) {
      goToRoute({
        mainPath: Routes.searchPage,
        params: [`query=${encodeURIComponent(hit.id)}`]
      })
      return
    } else if (indexName === IndexNameTypes.QUERY_SUGGESTIONS) {
      goToRoute({
        mainPath: Routes.searchPage,
        params: [`query=${encodeURIComponent(hit.query)}`]
      })
      return
    }
    goToRoute({
      mainPath: hit.urlPath || Routes.searchPage
    })
  }

  const renderText = (attribute = 'title', underlineLink = true) => {
    return underlineLink ? (
      <Link>
        <components.Highlight hit={hit} attribute={attribute} />
      </Link>
    ) : (
      <components.Highlight hit={hit} attribute={attribute} />
    )
  }

  const getText = () => {
    if (indexId === IndexIdTypes.RECENT_SEARCHES) {
      return <Link>{hit.label}</Link>
    } else if (indexName === IndexNameTypes.QUERY_SUGGESTIONS) {
      return components ? renderText('query') : <Link>{hit.query}</Link>
    } else if (indexId === IndexIdTypes.ARTICLES_SUPPORT) {
      return renderText('title', false)
    }
    return renderText('title')
  }

  return (
    <Container pt={['3', '4']}>
      <Button
        icon={Icon.images.arrowRight}
        iconPosition={Button.iconPositions.right}
        onClickHandler={clickHandler}
        text={getText()}
        variant={Button.variants.tertiary}
      />
    </Container>
  )
}

const ProductCard = ({
  hit,
  components,
  translations,
  fullWidth,
  sendEvent,
  fireSearchClickGA
}) => {
  const HighlightComp = components ? components.Highlight : Highlight

  const clickHandler = () => {
    fireSearchClickGA()
    sendEvent && sendEvent()
    const productUrl = getDynamicPath(Routes.products, hit.productID)
    goToRoute({
      mainPath: productUrl,
      params: [`variantid=${hit.variantID}`]
    })
  }

  return (
    <Container
      width={fullWidth ? 'full' : ['36', '48']}
      onClickHandler={clickHandler}>
      <Container
        fullWidth
        position={Container.positions.relative}
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.center}
        height={['36', '48']}>
        <LazyImage src={hit.imageURL} bgLayer={false} />
      </Container>
      <TextCaption size={TextCaption.sizes.m}>
        <HighlightComp hit={hit} attribute="name" />
      </TextCaption>
      <TextBody>{`${formatCurrency(hit.price)} ${
        translations.tk_taxLabel
      }`}</TextBody>
    </Container>
  )
}

const getTitleAttribute = hit => {
  if (isAV() && hit.postLoginTitle) {
    return 'postLoginTitle'
  }
  return 'title'
}

const getDescription = hit => {
  if (isAV() && hit.postLoginDescription) {
    return hit.postLoginDescription
  }
  return hit.description
}

const getImageSrc = hit => {
  if (isAV() && hit.postLoginImageURL) {
    return hit.postLoginImageURL
  }
  return hit.imageURL
}

const ArticleCard = ({ hit, sendEvent, fireSearchClickGA }) => {
  const clickHandler = () => {
    fireSearchClickGA()
    sendEvent()
    goToRoute({
      mainPath: hit.urlPath
    })
  }

  return (
    <Container fullWidth onClickHandler={clickHandler}>
      <Container
        fullWidth
        position={Container.positions.relative}
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.center}
        height={['48', '40']}>
        <LazyImage
          cover
          width="full"
          height="full"
          src={getImageSrc(hit)}
          bgLayer={false}
        />
      </Container>
      <Container pt="2">
        <TextCaption size={TextCaption.sizes.l}>
          <Highlight hit={hit} attribute={getTitleAttribute(hit)} />
        </TextCaption>
      </Container>
      <Container pt="2">
        <TextBody size={TextCaption.sizes.xs} breakWords>
          {getDescription(hit)}
        </TextBody>
      </Container>
    </Container>
  )
}

const Article = ({ hit, sendEvent, fireSearchClickGA }) => {
  const clickHandler = () => {
    fireSearchClickGA()
    sendEvent()
    goToRoute({
      mainPath: hit.urlPath
    })
  }

  return (
    <Container fullWidth onClickHandler={clickHandler}>
      <TextCaption size={TextCaption.sizes.m}>
        <Link>
          <Highlight hit={hit} attribute={getTitleAttribute(hit)} />
        </Link>
      </TextCaption>
      <Container pt="2">
        <TextBody size={TextCaption.sizes.s} breakWords>
          {getDescription(hit)}
        </TextBody>
      </Container>
    </Container>
  )
}

const RecommendedSection = ({
  recommendedContent,
  translations,
  initialQuerySuggestionsHits,
  currentQuery
}) => {
  const clickHandler = (el, i) => {
    DcxAnalytics.searchResultClickEvent(
      currentQuery || '',
      i,
      el.title,
      RECOMMENDED_CONTENT_ID
    )
    goToRoute({
      mainPath: el.url
    })
  }
  const visibleRecommendedContent =
    recommendedContent.filter(
      item =>
        item.visibility[!isAuthenticated() ? 'guest' : isAV() ? 'av' : 'asd']
    ) || []

  return (
    <Container>
      {initialQuerySuggestionsHits && initialQuerySuggestionsHits.length ? (
        <Container id={INITIAL_QUERY_SUGGESTIONS_ID}>
          <Header text={translations.tk_recommendedSearchHeader} />
          {initialQuerySuggestionsHits.map((item, index) => (
            <ItemLink
              key={index}
              hit={item}
              indexName={IndexNameTypes.QUERY_SUGGESTIONS}
              fireSearchClickGA={() =>
                DcxAnalytics.searchResultClickEvent(
                  currentQuery || '',
                  item.__position,
                  item.title || item.query,
                  IndexNameTypes.QUERY_SUGGESTIONS
                )
              }
            />
          ))}
        </Container>
      ) : null}
      {visibleRecommendedContent && visibleRecommendedContent.length ? (
        <Container>
          <Header text={translations.tk_recommendedContentHeader} />
          <Css
            id={RECOMMENDED_CONTENT_ID}
            s={{
              overflowX: 'auto',
              scrollbarWidth: 'none',
              cursor: 'pointer',
              paddingTop: '20px'
            }}>
            <Css
              s={{
                display: 'flex',
                width: 'max-content'
              }}>
              {visibleRecommendedContent.map((el, i) => (
                <Container
                  key={i}
                  pr={recommendedContent.length - 1 !== i ? '14' : '0'}>
                  <Container
                    additionalClasses={'q-max-w-xxs'}
                    onClickHandler={() => {
                      clickHandler(el, i)
                    }}>
                    <Container position={Container.positions.relative}>
                      <LazyImage src={el?.image} height="32" bgLayer={false} />
                    </Container>
                    <Container pt="2">
                      <TextTitle heading={TextTitle.headings.h6}>
                        {el?.title}
                      </TextTitle>
                    </Container>
                    <Container wordBreak={Container.wordBreaks.breakAll} pt="2">
                      <Link>{el?.linkText}</Link>
                    </Container>
                  </Container>
                </Container>
              ))}
            </Css>
          </Css>
        </Container>
      ) : null}
    </Container>
  )
}

const NoResults = ({ translations }) => (
  <Container display={Container.displays.flex} pt="10">
    <Container pr="4" pt="2">
      <State size={State.sizes.s} state={State.states.warningTurquoise} />
    </Container>
    <Container>
      <TextTitle heading={TextTitle.headings.h6}>
        {translations.tk_noResultsTitle}
      </TextTitle>
      <TextBody {...htmlTranslation(translations, 'tk_noResultsMsg')} />
    </Container>
  </Container>
)

const LoadMoreButton = ({ translations, onLoadMore, ...props }) => {
  const { items, showMore, isLastPage } = useInfiniteHits(props)

  if (!items.length) return

  return (
    <Container
      pt="10"
      display={Container.displays.flex}
      justifyContent={Container.justifyContentValues.center}>
      <Button
        onClickHandler={() => {
          showMore()
          onLoadMore()
        }}
        text={translations.tk_loadMore}
        variant={Button.variants.secondary}
        disabled={isLastPage}
      />
    </Container>
  )
}

export {
  Article,
  ArticleCard,
  Header,
  ItemLink,
  ProductCard,
  RecommendedSection,
  NoResults,
  LoadMoreButton
}
